import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { RootState } from '@/store';
import { API_ENDPOINTS, API_SLICE, BASE_URL } from '@/utils/constants/apiEndpoints';

import {
  AccountOverviewQuery,
  AccountOverviewResponse,
  AlertQuery,
  AlertResponse,
  BatteryLifeApiParams,
  BatteryLifeQueryParams,
  BatteryLifeResponse,
  BillingQuery,
  BillingResponse,
  DeviceRecallApiParams,
  DeviceRecallQueryParams,
  DeviceRecallResponse,
  DownloadReportQuery,
  DownloadReportResponse,
  LeadRecallApiParams,
  LeadRecallQueryParams,
  LeadRecallResponse,
  SignaturesQuery,
  SignaturesResponse
} from '@/types/analytics';

const transformDeviceRecallParams = (params: DeviceRecallQueryParams): DeviceRecallApiParams => {
  try {
    if (!params.limit || params.offset === undefined) {
      console.error('Missing limit or offset');
      throw new Error('Missing required pagination params');
    }

    const page = Math.floor(params.offset / params.limit) + 1;

    // Extract sortBy and sortDirection from orderBy
    let sortBy = 'patientName'; // default
    let sortDirection: 'ASC' | 'DESC' = 'DESC'; // default

    if (params.orderBy && typeof params.orderBy === 'object') {
      const orderByValues = Object.values(params.orderBy);
      if (orderByValues.length > 0 && orderByValues[0]) {
        sortBy = orderByValues[0].field;
        sortDirection = orderByValues[0].direction.toUpperCase() as 'ASC' | 'DESC';
      }
    }

    const filters: DeviceRecallApiParams['filters'] = {
      clinicIds: params.clinicIds,
      recallDateRange: params.dateRange
    };

    if (params.filters && Array.isArray(params.filters)) {
      params.filters.forEach((filter) => {
        switch (filter.field) {
          case 'vendor':
            filters.deviceVendor = filter.arguments;
            break;
          case 'deviceType':
            filters.deviceType = filter.arguments;
            break;
          case 'recallClass':
            filters.recallClass = filter.arguments;
            break;
          case 'recallStatus':
            filters.recallStatus = filter.arguments;
            break;
          case 'leadType':
            filters.leadType = filter.arguments;
            break;
        }
      });
    }

    const result = {
      sortBy,
      sortDirection,
      page,
      limit: params.limit,
      filters,
      columns: params.columns
    };
    return result;
  } catch (error) {
    console.error('Error in transformDeviceRecallParams:', error);
    throw error;
  }
};

const transformLeadRecallParams = (params: LeadRecallQueryParams): LeadRecallApiParams => {
  try {
    if (!params.limit || params.offset === undefined) {
      throw new Error('Missing required pagination params');
    }

    const page = Math.floor(params.offset / params.limit) + 1;

    let sortBy = 'patientName'; // default
    let sortDirection: 'ASC' | 'DESC' = 'DESC'; // default

    if (params.orderBy && typeof params.orderBy === 'object') {
      const orderByValues = Object.values(params.orderBy);
      if (orderByValues.length > 0 && orderByValues[0]) {
        sortBy = orderByValues[0].field;
        sortDirection = orderByValues[0].direction.toUpperCase() as 'ASC' | 'DESC';
      }
    }

    const filters: LeadRecallApiParams['filters'] = {
      clinicIds: params.clinicIds,
      recallDateRange: params.dateRange
    };

    if (params.filters && Array.isArray(params.filters)) {
      params.filters.forEach((filter) => {
        switch (filter.field) {
          case 'vendor':
            filters.deviceVendor = filter.arguments;
            break;
          case 'deviceType':
            filters.deviceType = filter.arguments;
            break;
          case 'leadType':
            filters.leadType = filter.arguments;
            break;
          case 'recallClass':
            filters.recallClass = filter.arguments;
            break;
          case 'recallStatus':
            filters.recallStatus = filter.arguments;
            break;
        }
      });
    }

    const result = {
      sortBy,
      sortDirection,
      page,
      limit: params.limit,
      filters
    };
    return result;
  } catch (error) {
    console.error('Error in transformLeadRecallParams:', error);
    throw error;
  }
};

const transformBatteryLifeParams = (params: BatteryLifeQueryParams): BatteryLifeApiParams => {
  try {
    if (!params.limit || params.offset === undefined) {
      throw new Error('Missing required pagination params');
    }

    const page = Math.floor(params.offset / params.limit) + 1;

    let sortBy = 'accountName';
    let sortDirection: 'ASC' | 'DESC' = 'DESC';

    if (params.orderBy && typeof params.orderBy === 'object') {
      const orderByValues = Object.values(params.orderBy);
      if (orderByValues.length > 0 && orderByValues[0]) {
        sortBy = orderByValues[0].field;
        sortDirection = orderByValues[0].direction.toUpperCase() as 'ASC' | 'DESC';
      }
    }

    const filters: BatteryLifeApiParams['filters'] = {
      clinicIds: params.clinicIds
    };

    if (params.filters && Array.isArray(params.filters)) {
      params.filters.forEach((filter) => {
        switch (filter.field) {
          case 'vendor':
            filters.deviceVendor = filter.arguments as string[];
            break;
          case 'deviceType':
            filters.deviceType = filter.arguments as string[];
            break;
          case 'batteryLongevity':
            filters.batteryLongevity = {
              operator: filter.operator as '<' | '<=' | '>' | '>=' | '=',
              months:
                typeof filter.arguments === 'number'
                  ? filter.arguments
                  : (filter.arguments as any).months
            };
            break;
        }
      });
    }

    const result = {
      sortBy,
      sortDirection,
      page,
      limit: params.limit,
      filters
    };
    return result;
  } catch (error) {
    console.error('Error in transformBatteryLifeParams:', error);
    throw error;
  }
};

const transformDeviceRecallDownloadParams = (params: {
  dateRange: [string, string];
  clinicIds: string[];
  columns: string[];
  filters?: Array<{ field: string; arguments: any }>;
}) => {
  const filters: any = {
    clinicIds: params.clinicIds,
    recallDateRange: params.dateRange
  };

  if (params.filters && Array.isArray(params.filters)) {
    params.filters.forEach((filter) => {
      switch (filter.field) {
        case 'vendor':
          filters.deviceVendor = filter.arguments;
          break;
        case 'deviceType':
          filters.deviceType = filter.arguments;
          break;
        case 'recallClass':
          filters.recallClass = filter.arguments;
          break;
        case 'recallStatus':
          filters.recallStatus = filter.arguments;
          break;
        case 'leadType':
          filters.leadType = filter.arguments;
          break;
      }
    });
  }

  return {
    sortBy: 'patientName',
    sortDirection: 'DESC' as const,
    filters,
    columns: params.columns
  };
};

const transformLeadRecallDownloadParams = (params: {
  dateRange: [string, string];
  clinicIds: string[];
  columns: string[];
  filters?: Array<{ field: string; arguments: any }>;
}) => {
  const filters: any = {
    clinicIds: params.clinicIds,
    recallDateRange: params.dateRange
  };

  if (params.filters && Array.isArray(params.filters)) {
    params.filters.forEach((filter) => {
      switch (filter.field) {
        case 'vendor':
          filters.deviceVendor = filter.arguments;
          break;
        case 'deviceType':
          filters.deviceType = filter.arguments;
          break;
        case 'leadType':
          filters.leadType = filter.arguments;
          break;
        case 'recallClass':
          filters.recallClass = filter.arguments;
          break;
        case 'recallStatus':
          filters.recallStatus = filter.arguments;
          break;
      }
    });
  }

  return {
    sortBy: 'patientName',
    sortDirection: 'DESC' as const,
    filters,
    columns: params.columns
  };
};

const transformBatteryLifeDownloadParams = (params: {
  dateRange?: [string, string];
  clinicIds: string[];
  columns: string[];
  filters?: Array<{ field: string; arguments: any; operator?: string }>;
  batteryLifePeriod?: { operator: '<' | '<=' | '>' | '>=' | '='; months: number };
}) => {
  const filters: any = {
    clinicIds: params.clinicIds
  };

  // Handle batteryLongevity from filters array
  if (params.filters && Array.isArray(params.filters)) {
    params.filters.forEach((filter) => {
      switch (filter.field) {
        case 'vendor':
          filters.deviceVendor = filter.arguments;
          break;
        case 'deviceType':
          filters.deviceType = filter.arguments;
          break;
        case 'batteryLongevity':
          filters.batteryLongevity = {
            operator: filter.operator as '<' | '<=' | '>' | '>=' | '=',
            months:
              typeof filter.arguments === 'number' ? filter.arguments : filter.arguments.months
          };
          break;
      }
    });
  }

  if (params.batteryLifePeriod && !filters.batteryLongevity) {
    filters.batteryLongevity = {
      operator: params.batteryLifePeriod.operator,
      months: params.batteryLifePeriod.months
    };
  }

  return {
    sortBy: 'accountName',
    sortDirection: 'DESC' as const,
    filters,
    columns: params.columns
  };
};

export const analyticsApiSlice = createApi({
  reducerPath: 'analyticsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  tagTypes: [API_SLICE.Analytics],
  endpoints: (builder) => ({
    getAccountOverview: builder.query<AccountOverviewResponse, AccountOverviewQuery>({
      query: (params) => ({
        url: API_ENDPOINTS.ANALYTICS.ACCOUNT_OVERVIEW,
        method: 'POST',
        body: params
      })
    }),
    getAlerts: builder.query<AlertResponse, AlertQuery>({
      query: (params) => ({
        url: API_ENDPOINTS.ANALYTICS.ALERTS,
        method: 'POST',
        body: params
      })
    }),
    getBillings: builder.query<BillingQuery, BillingResponse>({
      query: (params) => ({
        url: API_ENDPOINTS.ANALYTICS.BILLINGS,
        method: 'POST',
        body: params
      })
    }),
    getSignatures: builder.query<SignaturesResponse, SignaturesQuery>({
      query: (params) => ({
        url: API_ENDPOINTS.ANALYTICS.SIGNATURES,
        method: 'POST',
        body: params
      })
    }),
    getDeviceRecalls: builder.query<DeviceRecallResponse, DeviceRecallQueryParams>({
      query: (params) => {
        const apiParams = transformDeviceRecallParams(params);
        return {
          url: API_ENDPOINTS.ANALYTICS.DEVICE_RECALLS,
          method: 'POST',
          body: apiParams
        };
      },
      transformResponse: (response: any) => {
        return {
          data: response.data,
          pagination: {
            fetchAll: false,
            limit: response.limit,
            offset: (response.page - 1) * response.limit,
            total: response.total
          }
        };
      }
    }),
    getLeadRecalls: builder.query<LeadRecallResponse, LeadRecallQueryParams>({
      query: (params) => {
        const apiParams = transformLeadRecallParams(params);
        return {
          url: API_ENDPOINTS.ANALYTICS.LEADS_RECALLS,
          method: 'POST',
          body: apiParams
        };
      },
      transformResponse: (response: any) => {
        return {
          data: response.data,
          pagination: {
            fetchAll: false,
            limit: response.limit,
            offset: (response.page - 1) * response.limit,
            total: response.total
          }
        };
      },
      keepUnusedDataFor: 0
    }),
    getBatteryLife: builder.query<BatteryLifeResponse, BatteryLifeQueryParams>({
      query: (params) => {
        const apiParams = transformBatteryLifeParams(params);
        return {
          url: API_ENDPOINTS.ANALYTICS.BATTERY_LIFE,
          method: 'POST',
          body: apiParams
        };
      },
      transformResponse: (response: any) => {
        return {
          data: response.data,
          pagination: {
            fetchAll: false,
            limit: response.limit,
            offset: (response.page - 1) * response.limit,
            total: response.total
          }
        };
      },
      keepUnusedDataFor: 0
    }),
    downloadReport: builder.mutation<DownloadReportResponse, DownloadReportQuery>({
      query: (params) => {
        let transformedQuery: any;

        // Transform query based on reportType
        switch (params.reportType) {
          case 'deviceRecalls':
            transformedQuery = transformDeviceRecallDownloadParams(params.query as any);
            break;
          case 'leadsRecalls':
            transformedQuery = transformLeadRecallDownloadParams(params.query as any);
            break;
          case 'batteryLife':
            transformedQuery = transformBatteryLifeDownloadParams(params.query as any);
            break;
          default:
            // For other report types (overview, alerts, billable, signatureStatus)
            transformedQuery = params.query;
        }

        return {
          url: API_ENDPOINTS.ANALYTICS.DOWNLOAD,
          method: 'POST',
          body: {
            reportType: params.reportType,
            query: transformedQuery
          }
        };
      }
    })
  })
});

export const {
  useGetAccountOverviewQuery,
  useGetAlertsQuery,
  useGetBillingsQuery,
  useGetSignaturesQuery,
  useGetDeviceRecallsQuery,
  useGetLeadRecallsQuery,
  useGetBatteryLifeQuery,
  useDownloadReportMutation
} = analyticsApiSlice;
